import moment from 'moment'
import React, {useMemo, useState} from 'react'
import DataTable, {
  Column,
} from '../../../../Components/shared/data-table/src/app/Components/shared/data-table/table'
import {convertToMiliseconds} from '../../../UserPages/contact-collection/utils'
import {Subscription, UserSubscription} from '../../../../../services/cellcast.v2.types'
import {
  useCreateSubscriptionByAdminMutation,
  useUpdateSubscriptionByAdminMutation,
  useRemoveSubscriptionByAdminMutation,
} from '../../../../../services/cellcast.v2'
import {toast} from 'react-toastify'
import {OverlayTrigger, Tooltip, Button, Modal, Form} from 'react-bootstrap'
import Select from 'react-select'
import {useProductSubscription} from './useProductSubscription'

const MAX_TRIAL_DAYS = 730
interface ISubscriptionWithId extends Subscription {
  id: string
  index?: number
}

const SubscriptionTable = ({
  subscriptionsData,
  userId,
  onRefetch,
}: {
  subscriptionsData: UserSubscription
  userId: string
  onRefetch?: (page?: number, limit?: number) => void
}) => {
  const {subscriptions} = subscriptionsData

  const {productPlans} = useProductSubscription('bcard')

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState<ISubscriptionWithId | null>(null)
  const [subscriptionToRemove, setSubscriptionToRemove] = useState<ISubscriptionWithId | null>(null)

  const [createForm, setCreateForm] = useState({
    planId: '',
    trialDays: 0,
  })
  const [editForm, setEditForm] = useState({
    planId: '',
    trialDays: 0,
  })

  const [createFormErrors, setCreateFormErrors] = useState({
    trialDays: false,
    trialDaysRequired: false,
  })
  const [editFormErrors, setEditFormErrors] = useState({
    trialDays: false,
    trialDaysRequired: false,
  })

  // API mutations
  const [createSubscription, {isLoading: isCreating}] = useCreateSubscriptionByAdminMutation()
  const [updateSubscription, {isLoading: isUpdating}] = useUpdateSubscriptionByAdminMutation()
  const [removeSubscription, {isLoading: isRemoving}] = useRemoveSubscriptionByAdminMutation()

  // Validation functions
  const validateTrialDays = (trialDays: number) => {
    return trialDays <= MAX_TRIAL_DAYS && trialDays >= 0
  }

  const handleCreateTrialDaysChange = (inputValue: string) => {
    const value = inputValue === '' ? 0 : parseInt(inputValue) || 0
    setCreateForm({...createForm, trialDays: value})
  }

  const handleCreateTrialDaysBlur = (inputValue: string) => {
    const value = inputValue === '' ? 0 : parseInt(inputValue) || 0
    const isEmpty = inputValue === ''
    const isInvalid = !validateTrialDays(value)

    setCreateFormErrors({
      trialDays: isInvalid,
      trialDaysRequired: isEmpty,
    })
  }

  const handleEditTrialDaysChange = (inputValue: string) => {
    const value = inputValue === '' ? 0 : parseInt(inputValue) || 0
    setEditForm({...editForm, trialDays: value})
  }

  const handleEditTrialDaysBlur = (inputValue: string) => {
    const value = inputValue === '' ? 0 : parseInt(inputValue) || 0
    const isEmpty = inputValue === ''
    const isInvalid = !validateTrialDays(value)

    setEditFormErrors({
      trialDays: isInvalid,
      trialDaysRequired: isEmpty,
    })
  }

  const subscriptionItems = useMemo(() => subscriptions?.items ?? [], [subscriptions])
  const total = subscriptions?.total ?? 0
  const serverLimit = subscriptions?.limit ?? 10
  const current = subscriptions?.current ?? 1

  const [page, setPage] = useState<number>(Math.max((current || 1) - 1, 0))
  const [pageSize, setPageSize] = useState<number>(serverLimit || 10)

  const subscriptionRows: ISubscriptionWithId[] = useMemo(
    () =>
      subscriptionItems.map((subscription: any, idx: number) => ({
        ...subscription,
        id: subscription._id || idx.toString(),
        index: idx + 1,
      })),
    [subscriptionItems]
  )

  const statusBadgeClass = (status: string) => {
    switch ((status || '').toLowerCase()) {
      case 'active':
        return 'badge badge-light-success fw-bold'
      case 'inactive':
      case 'canceled':
        return 'badge badge-light-danger fw-bold'
      case 'trialing':
        return 'badge badge-light-warning fw-bold'
      default:
        return 'badge badge-light-secondary fw-bold'
    }
  }

  // Handlers
  const handleCreateSubscription = async () => {
    try {
      const selectedPlan = productPlans.find((plan) => plan._id === createForm.planId)
      if (!selectedPlan) {
        toast.error('Please select a valid plan')
        return
      }

      // Validate trial days limit (max 730 days / 2 years)
      if (createForm.trialDays > MAX_TRIAL_DAYS) {
        toast.error('Trial period must be 730 days (2 years) or less')
        return
      }

      await createSubscription({
        productId: selectedPlan.productId._id,
        planId: createForm.planId,
        trialDays: createForm.trialDays,
        userId: userId,
      }).unwrap()

      toast.success('Subscription created successfully')
      setShowCreateModal(false)
      setCreateForm({planId: '', trialDays: 0})
      setCreateFormErrors({trialDays: false, trialDaysRequired: false})

      // Refetch subscription data
      if (onRefetch) {
        onRefetch()
      }
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to create subscription')
    }
  }

  const handleUpdateSubscription = async () => {
    if (!selectedSubscription) return

    try {
      if (editForm.trialDays > MAX_TRIAL_DAYS) {
        toast.error('Trial period must be 730 days (2 years) or less')
        return
      }

      await updateSubscription({
        stripeSubscriptionId: selectedSubscription.stripeSubscriptionId,
        trialDays: editForm.trialDays,
        planId: editForm.planId,
      }).unwrap()

      toast.success('Subscription updated successfully')
      setShowEditModal(false)
      setSelectedSubscription(null)
      setEditFormErrors({trialDays: false, trialDaysRequired: false})

      // Refetch subscription data
      if (onRefetch) {
        onRefetch()
      }
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to update subscription')
    }
  }

  const handleRemoveSubscription = async (subscription: ISubscriptionWithId) => {
    if (!subscription.stripeSubscriptionId) {
      toast.error('Invalid subscription ID')
      return
    }

    try {
      await removeSubscription({
        stripeSubscriptionId: subscription.stripeSubscriptionId,
      }).unwrap()

      toast.success('Subscription removed successfully')

      // Refetch subscription data
      if (onRefetch) {
        onRefetch()
      }
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to remove subscription')
    }
  }

  const handleEditClick = (subscription: ISubscriptionWithId) => {
    setSelectedSubscription(subscription)
    setEditForm({
      planId: subscription?.planId?._id || '',
      trialDays: subscription.trialDays || 0,
    })
    setShowEditModal(true)
  }

  const handleRemoveClick = (subscription: ISubscriptionWithId) => {
    setSubscriptionToRemove(subscription)
    setShowRemoveModal(true)
  }

  const confirmRemove = async () => {
    if (!subscriptionToRemove) return

    await handleRemoveSubscription(subscriptionToRemove)
    setShowRemoveModal(false)
    setSubscriptionToRemove(null)
  }

  const subscriptionColumns: Column<ISubscriptionWithId>[] = [
    {
      id: 'product',
      label: 'PRODUCT',
      width: 200,
      Cell: (row) => (
        <div className='d-flex align-items-center'>
          <div className='symbol symbol-45px me-5'>
            <div
              className={`symbol-label bg-light-${
                row?.productId?.productKey === 'bcard' ? 'primary' : 'success'
              }`}
            >
              {row?.productId?.productKey === 'bcard' ? (
                <i className='bi bi-credit-card fs-2x text-primary'></i>
              ) : (
                <i className='bi bi-chat-dots fs-2x text-success'></i>
              )}
            </div>
          </div>
          <div className='d-flex justify-content-start flex-column'>
            <span className='text-dark fw-bolder text-hover-primary fs-6'>
              {row?.productId?.name || 'N/A'}
            </span>
            <span className='text-muted fw-bold text-muted d-block fs-7'>
              {row?.productId?.productKey || 'N/A'}
            </span>
          </div>
        </div>
      ),
    },
    {
      id: 'plan',
      label: 'PLAN',
      width: 180,
      Cell: (row) => (
        <div className='d-flex flex-column'>
          <span className='text-dark fw-bolder text-hover-primary d-block fs-6'>
            {row?.planId?.name || 'N/A'}
          </span>
          <span className='text-muted fw-bold text-muted d-block fs-7'>
            {row?.planId?.price && row?.planId?.billingInterval
              ? `$${row.planId.price}/${row.planId.billingInterval}`
              : 'N/A'}
          </span>
        </div>
      ),
    },
    {
      id: 'status',
      label: 'STATUS',
      width: 120,
      Cell: (row) => (
        <span className={statusBadgeClass(row.status)} style={{fontSize: 13}}>
          {row.status}
        </span>
      ),
    },
    {
      id: 'trial',
      label: 'TRIAL',
      width: 120,
      Cell: (row) => (
        <span>
          {row.isTrial ? (
            <span className='badge badge-light-info'>{row.trialDays} days</span>
          ) : (
            <span className='text-muted'>No trial</span>
          )}
        </span>
      ),
    },
    {
      id: 'periodStart',
      label: 'PERIOD START',
      width: 150,
      Cell: (row) => (
        <span>
          {row.currentPeriodStart
            ? moment(convertToMiliseconds(row.currentPeriodStart)).format('DD/MM/YYYY')
            : '-'}
        </span>
      ),
    },
    {
      id: 'periodEnd',
      label: 'PERIOD END',
      width: 150,
      Cell: (row) => (
        <span>
          {row.currentPeriodEnd
            ? moment(convertToMiliseconds(row.currentPeriodEnd)).format('DD/MM/YYYY')
            : '-'}
        </span>
      ),
    },
    {
      id: 'actions',
      label: 'ACTIONS',
      width: 100,
      Cell: (row) => (
        <div className='d-flex gap-2 p-2'>
          {!(row.status || '').toLowerCase().includes('cancel') && (
            <OverlayTrigger placement='top' overlay={<Tooltip>Edit subscription</Tooltip>}>
              <button
                className='btn btn-sm btn-icon btn-light btn-active-light-primary'
                onClick={(e) => {
                  e.stopPropagation()
                  handleEditClick(row)
                }}
                title='Edit Subscription'
              >
                <i className='bi bi-pencil'></i>
              </button>
            </OverlayTrigger>
          )}
          {!(row.status || '').toLowerCase().includes('cancel') && (
            <OverlayTrigger placement='top' overlay={<Tooltip>Remove subscription</Tooltip>}>
              <button
                className='btn btn-sm btn-icon btn-light btn-active-light-danger'
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemoveClick(row)
                }}
                title='Remove Subscription'
              >
                <i className='bi bi-trash'></i>
              </button>
            </OverlayTrigger>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className='card mb-6'>
      <div className='card-header card-header-stretch border-bottom-0'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-2 mb-1 small-title'>Subscriptions</span>
        </h3>
        <div className='card-toolbar m-0 d-flex align-items-center'>
          <Button variant='primary' size='sm' onClick={() => setShowCreateModal(true)}>
            <i className='bi bi-plus-circle me-2'></i>
            Create
          </Button>
        </div>
      </div>
      <div className='card-body p-0 pt-1'>
        <DataTable
          items={subscriptionRows}
          isLoading={false}
          columns={subscriptionColumns}
          sortData={{field: 'index', sort: 'asc'}}
          handleSortedChange={() => {}}
          pageSize={pageSize}
          totalData={total}
          handleChangePage={(newPage: number) => {
            setPage(newPage)
            if (onRefetch) onRefetch(newPage + 1, pageSize)
          }}
          handleChangSizeData={(newSize: number) => {
            setPageSize(newSize)
            setPage(0)
            if (onRefetch) onRefetch(1, newSize)
          }}
          page={page}
          onRowClick={() => {}}
          onRowHover={() => {}}
        />
      </div>

      {/* Create Subscription Modal */}
      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        size='lg'
        autoFocus
        backdrop='static'
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New Subscription</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>Product</Form.Label>
              <Form.Control type='text' value='BCard' disabled placeholder='BCard' />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Plan</Form.Label>
              <Select<{value: string; label: string}>
                value={
                  productPlans.find((plan) => plan._id === createForm.planId)
                    ? {
                        value: createForm.planId,
                        label:
                          productPlans.find((plan) => plan._id === createForm.planId)?.planKey ||
                          '',
                      }
                    : null
                }
                isSearchable={false}
                name='plan'
                options={productPlans.map((plan) => ({
                  value: plan._id,
                  label: plan.planKey,
                }))}
                onChange={(selected) => {
                  setCreateForm({...createForm, planId: selected ? selected.value : ''})
                }}
                placeholder='Select a plan'
              />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Trial Days</Form.Label>
              <Form.Control
                type='number'
                max={MAX_TRIAL_DAYS}
                value={createForm.trialDays === 0 ? '' : createForm.trialDays}
                onChange={(e) => handleCreateTrialDaysChange(e.target.value)}
                onBlur={(e) => handleCreateTrialDaysBlur(e.target.value)}
                placeholder='Enter trial days (max 730 days)'
                isInvalid={createFormErrors.trialDays || createFormErrors.trialDaysRequired}
              />
              {createFormErrors.trialDaysRequired && (
                <Form.Control.Feedback type='invalid'>Trial days is required</Form.Control.Feedback>
              )}
              {createFormErrors.trialDays && !createFormErrors.trialDaysRequired && (
                <Form.Control.Feedback type='invalid'>
                  Trial period must be between 0 and {MAX_TRIAL_DAYS} days (2 years)
                </Form.Control.Feedback>
              )}
              <Form.Text className='text-muted'>
                Maximum trial period is {MAX_TRIAL_DAYS} days (2 years)
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleCreateSubscription}
            disabled={
              isCreating ||
              !createForm.planId ||
              createFormErrors.trialDays ||
              createFormErrors.trialDaysRequired
            }
          >
            {isCreating ? 'Creating...' : 'Create Subscription'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Subscription Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size='lg'
        backdrop='static'
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Subscription</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>Product</Form.Label>
              <Form.Control type='text' value='BCard' disabled placeholder='BCard' />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Plan</Form.Label>
              <Select<{value: string; label: string}>
                value={
                  productPlans.find((plan) => plan._id === editForm.planId)
                    ? {
                        value: editForm.planId,
                        label:
                          productPlans.find((plan) => plan._id === editForm.planId)?.planKey || '',
                      }
                    : null
                }
                isSearchable={false}
                name='plan'
                options={productPlans.map((plan) => ({
                  value: plan._id,
                  label: plan.planKey,
                }))}
                onChange={(selected) => {
                  setEditForm({...editForm, planId: selected ? selected.value : ''})
                }}
                placeholder='Select a plan'
              />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Trial Days</Form.Label>
              <Form.Control
                type='number'
                max={MAX_TRIAL_DAYS}
                value={editForm.trialDays === 0 ? '' : editForm.trialDays}
                onChange={(e) => handleEditTrialDaysChange(e.target.value)}
                onBlur={(e) => handleEditTrialDaysBlur(e.target.value)}
                placeholder='Enter trial days (max 730 days)'
                isInvalid={editFormErrors.trialDays || editFormErrors.trialDaysRequired}
              />
              {editFormErrors.trialDaysRequired && (
                <Form.Control.Feedback type='invalid'>Trial days is required</Form.Control.Feedback>
              )}
              {editFormErrors.trialDays && !editFormErrors.trialDaysRequired && (
                <Form.Control.Feedback type='invalid'>
                  Trial period must be between 0 and {MAX_TRIAL_DAYS} days (2 years)
                </Form.Control.Feedback>
              )}
              <Form.Text className='text-muted'>
                Maximum trial period is {MAX_TRIAL_DAYS} days (2 years)
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleUpdateSubscription}
            disabled={
              isUpdating ||
              !editForm.planId ||
              editFormErrors.trialDays ||
              editFormErrors.trialDaysRequired
            }
          >
            {isUpdating ? 'Updating...' : 'Update Subscription'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Remove Subscription Modal */}
      <Modal show={showRemoveModal} onHide={() => setShowRemoveModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove Subscription</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='mb-0'>
            Are you sure you want to remove this subscription? This action cannot be undone.
          </p>
          <p className='text-danger mt-2 mb-0'>
            Removing this subscription will revoke access for current customers.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => {
              setShowRemoveModal(false)
              setSubscriptionToRemove(null)
            }}
          >
            Cancel
          </Button>
          <Button variant='danger' onClick={confirmRemove} disabled={isRemoving}>
            {isRemoving ? 'Removing...' : 'Remove'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default SubscriptionTable
